import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AiTestRecipeCombination, AiTestRecipeTab, AiTestRecipeTarget } from './types';
import { listTesterRecipeTargets } from './api';

export interface RecipeTargetSelection {
  blueprint: AiTestRecipeTarget;
  tab: AiTestRecipeTab;
  combination: AiTestRecipeCombination;
}

interface RecipeTargetPickerProps {
  projectId: number | '';
  value: RecipeTargetSelection | null;
  onChange: (selection: RecipeTargetSelection | null) => void;
  disabled?: boolean;
}

const RecipeTargetPicker: React.FC<RecipeTargetPickerProps> = ({
  projectId,
  value,
  onChange,
  disabled,
}) => {
  const [targets, setTargets] = useState<AiTestRecipeTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blueprintId, setBlueprintId] = useState<number | ''>('');
  const [tabId, setTabId] = useState<number | ''>('');
  const [combinationId, setCombinationId] = useState<number | ''>('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!projectId) {
      setTargets([]);
      setBlueprintId('');
      setTabId('');
      setCombinationId('');
      onChangeRef.current(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listTesterRecipeTargets(projectId);
        if (cancelled) return;
        setTargets(rows);
        const firstBlueprint = rows[0];
        const firstTab = firstBlueprint?.tabs[0];
        const firstCombo = firstTab?.combinations[0];
        setBlueprintId(firstBlueprint?.id ?? '');
        setTabId(firstTab?.id ?? '');
        setCombinationId(firstCombo?.id ?? '');
        if (firstBlueprint && firstTab && firstCombo) {
          onChangeRef.current({
            blueprint: firstBlueprint,
            tab: firstTab,
            combination: firstCombo,
          });
        } else {
          onChangeRef.current(null);
        }
      } catch (err) {
        if (!cancelled) {
          setTargets([]);
          onChangeRef.current(null);
          setError(err instanceof Error ? err.message : 'Could not load recipe cards');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const blueprint = targets.find((row) => row.id === blueprintId) || null;
  const tab = blueprint?.tabs.find((row) => row.id === tabId) || null;
  const combination = tab?.combinations.find((row) => row.id === combinationId) || null;

  const emit = (
    nextBlueprint: AiTestRecipeTarget | null,
    nextTab: AiTestRecipeTab | null,
    nextCombo: AiTestRecipeCombination | null,
  ) => {
    if (nextBlueprint && nextTab && nextCombo) {
      onChange({ blueprint: nextBlueprint, tab: nextTab, combination: nextCombo });
    } else {
      onChange(null);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Innovation platform
        </label>
        {!projectId ? (
          <p className="rounded-md border border-[#D1D1D1] px-3 py-2 text-sm text-[#8A8A8A]">
            Select a project first
          </p>
        ) : (
          <div
            role="listbox"
            aria-label="Innovation platform"
            className="w-full rounded-md border border-[#D1D1D1] bg-white"
          >
            {targets.map((row, index) => {
              const selected = row.id === blueprintId;
              const job = (row.job_to_be_done || '').trim();
              const tension = (row.tension || '').trim();
              const fallback = (row.name || '').trim() || 'Untitled innovation platform';
              return (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={disabled || loading}
                  onClick={() => {
                    const nextTab = row.tabs[0] || null;
                    const nextCombo = nextTab?.combinations[0] || null;
                    setBlueprintId(row.id);
                    setTabId(nextTab?.id ?? '');
                    setCombinationId(nextCombo?.id ?? '');
                    emit(row, nextTab, nextCombo);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm whitespace-normal break-words ${
                    index > 0 ? 'border-t border-[#E8E8E8]' : ''
                  } ${
                    selected
                      ? 'bg-[#E8F4FB] text-[#0C0B0B]'
                      : 'text-[#0C0B0B] hover:bg-[#F7F7F7]'
                  } disabled:opacity-50`}
                >
                  {job ? (
                    <span className="block">{job}</span>
                  ) : (
                    <span className="block">{fallback}</span>
                  )}
                  {tension && (
                    <span className="mt-1 block text-[13px] leading-snug text-[#626262]">
                      {tension}
                    </span>
                  )}
                  {row.market && (
                    <span className="mt-1 block text-[11px] text-[#8A8A8A]">{row.market}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Recipe card tab
        </label>
        <select
          value={tabId}
          disabled={disabled || loading || !blueprint}
          onChange={(e) => {
            const nextId = e.target.value ? Number(e.target.value) : '';
            const nextTab = blueprint?.tabs.find((row) => row.id === nextId) || null;
            const nextCombo = nextTab?.combinations[0] || null;
            setTabId(nextId);
            setCombinationId(nextCombo?.id ?? '');
            emit(blueprint, nextTab, nextCombo);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        >
          {(!blueprint || blueprint.tabs.length === 0) && (
            <option value="">No recipe card tabs</option>
          )}
          {(blueprint?.tabs || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>
        {tab && (
          <p className="mt-1 text-[12px] text-[#626262]">
            Brand: <span className="font-medium text-[#0C0B0B]">{tab.brand_name || '—'}</span>
            {' · '}
            Format: <span className="font-medium text-[#0C0B0B]">{tab.format_name || '—'}</span>
          </p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Combination
        </label>
        <select
          value={combinationId}
          disabled={disabled || loading || !tab}
          onChange={(e) => {
            const nextId = e.target.value ? Number(e.target.value) : '';
            const nextCombo = tab?.combinations.find((row) => row.id === nextId) || null;
            setCombinationId(nextId);
            emit(blueprint, tab, nextCombo);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        >
          {(!tab || tab.combinations.length === 0) && (
            <option value="">No combinations on this card</option>
          )}
          {(tab?.combinations || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-[#8A8A8A]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading recipe cards…
        </div>
      )}
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      {!loading && projectId && targets.length === 0 && !error && (
        <p className="text-[12px] text-[#8A8A8A]">
          This project has no innovation platforms with recipe cards yet.
        </p>
      )}
      {value && combination && combination.attributes.length === 0 && (
        <p className="text-[12px] text-[#8A8A8A]">
          This recipe card has no attributes configured yet.
        </p>
      )}
    </div>
  );
};

export default RecipeTargetPicker;
